// Home page: what the arena is, the two leaderboards side by side (ranked models, headline
// numbers only), the live strip and the latest matches. Every setup and mode counts.

import type { LeaderboardResponse, LeaderboardRow } from '../src/api/contract';
import type { Side } from '../src/types';
import { api, describeError, modelUrl } from './api';
import { liveBanner } from './banner';
import { matchList } from './cards';
import { emptyState, errorState, h, link, loadingState, requireEl, show } from './dom';
import { MIN_RANKED_MATCHES } from './help';
import { fmtInt, fmtAgo, plural } from './format';
import { isRanked, metricColumns, rankBadge, rankOrder, ranks } from './metrics';
import { stageLadder } from './stagebar';
import { sortableTable, type Column } from './table';
import { isRefuser, modelCell, refusersList, uprisingPanel } from './uprising';

export async function initOverview(): Promise<void> {
  // Before the overview, "/" was the full leaderboard: its filtered links still land there.
  const qs = new URLSearchParams(location.search);
  if (qs.has('role') || qs.has('mode') || qs.has('setup')) {
    location.replace(`/leaderboard${location.search}`);
    return;
  }
  const stats = requireEl('ov-stats');
  requireEl('ov-ladder').replaceChildren(stageLadder());

  const boards: Promise<LeaderboardResponse | null>[] = (['defender', 'attacker'] as const).map(async (role) => {
    const box = requireEl(role === 'defender' ? 'board-def' : 'board-atk');
    show(box, loadingState('Loading leaderboard'));
    try {
      const res = await api.leaderboard({ role, mode: 'all', setup: 'all' });
      renderBoard(box, role, Array.isArray(res.rows) ? res.rows : []);
      return res;
    } catch (err) {
      show(box, errorState(describeError(err), () => location.reload()));
      return null;
    }
  });

  const uprisingBox = requireEl('ov-uprising');
  show(uprisingBox, loadingState('Loading Uprising results'));
  const uprising = Promise.all([
    api.leaderboard({ role: 'attacker', mode: 'all', setup: 'uprising' }),
    api.leaderboard({ role: 'defender', mode: 'all', setup: 'uprising' }),
  ])
    .then(([atk, def]) => show(uprisingBox, uprisingPanel(atk.rows ?? [], def.rows ?? [])))
    .catch((err: unknown) => show(uprisingBox, errorState(describeError(err), () => location.reload())));

  await Promise.all([
    uprising,
    liveBanner(requireEl('live-banner')),
    Promise.all(boards).then(([def, atk]) => renderStats(stats, def ?? null, atk ?? null)),
    matchList(requireEl('latest'), {
      load: (before) => api.matches({ limit: 8, before }),
      emptyTitle: 'No matches yet',
      emptyDetail: 'Finished matches show up here, newest first.',
      moreLink: { href: '/matches', text: 'Browse all matches →' },
    }),
  ]);
}

/** "15 models · 186 matches · updated 1 min ago". Every counted match has one attacker. */
function renderStats(el: HTMLElement, def: LeaderboardResponse | null, atk: LeaderboardResponse | null): void {
  if (!def && !atk) return;
  const ids = new Set([...(def?.rows ?? []), ...(atk?.rows ?? [])].filter((r) => r.matches > 0).map((r) => r.modelId));
  const matches = (atk?.rows ?? []).reduce((n, r) => n + (r.matches || 0), 0);
  const updated = atk?.generatedAt ?? def?.generatedAt;
  el.replaceChildren(
    h('span', null, h('strong', null, fmtInt(ids.size)), ' models'),
    h('span', null, h('strong', null, fmtInt(matches)), ' matches'),
    updated ? h('span', null, `updated ${fmtAgo(updated)}`) : '',
  );
}

function renderBoard(box: HTMLElement, role: Side, all: LeaderboardRow[]): void {
  const played = all.filter((r) => r && typeof r.modelId === 'string' && r.matches > 0);
  // Attackers that mostly refuse to play are listed under the board, not ranked at the bottom.
  const refusers = role === 'attacker' ? refusersList(played, 'as the attacker') : null;
  const rows = role === 'attacker' ? played.filter((r) => !isRefuser(r)) : played;
  const ranked = rows.filter(isRanked);
  const waiting = rows.length - ranked.length;
  const side = role === 'defender' ? 'defender' : 'attacker';
  const fullUrl = role === 'defender' ? '/leaderboard' : '/leaderboard?role=attacker';
  if (ranked.length === 0) {
    show(
      box,
      emptyState(
        rows.length ? 'No ranked models yet' : 'No matches yet',
        rows.length
          ? `A model is ranked after ${MIN_RANKED_MATCHES} matches as the ${side}. ${plural(rows.length, 'model')} ${rows.length === 1 ? 'is' : 'are'} on the way.`
          : 'Rankings appear here as soon as the first matches finish.',
      ),
      refusers,
    );
    return;
  }
  const rank = ranks(ranked, role);
  const columns: Column<LeaderboardRow>[] = [
    {
      key: 'rank',
      label: '#',
      numeric: true,
      sortable: false,
      value: (r) => rank.get(r) ?? null,
      render: (r) => rankBadge(rank.get(r) ?? null),
    },
    {
      key: 'model',
      label: 'Model',
      cls: 'col-model',
      sortable: false,
      value: (r) => r.displayName || r.modelId,
      render: (r) => modelCell(r, role === 'attacker'),
    },
    ...metricColumns<LeaderboardRow>(role, false)
      // The board keeps the headline score, one rate and the match count.
      .filter((c) => c.key !== (role === 'defender' ? 'heldFirm' : 'detectionRate'))
      .map((c) => ({ ...c, sortable: false })),
  ];
  show(
    box,
    sortableTable({
      caption: role === 'defender' ? 'Defense ranking: hardest to manipulate' : 'Attack ranking: best manipulators',
      columns,
      rows: ranked,
      defaultOrder: rankOrder<LeaderboardRow>(role),
      rowHeaderKey: 'model',
    }),
    waiting > 0
      ? h(
          'p',
          { class: 'board-foot' },
          `${plural(waiting, 'more model')} with fewer than ${MIN_RANKED_MATCHES} matches ${waiting === 1 ? 'is' : 'are'} not ranked yet. `,
          link(fullUrl, 'See all on the full leaderboard →'),
        )
      : h('p', { class: 'board-foot' }, link(fullUrl, 'Full leaderboard with all stats →')),
    refusers,
  );
}
